import { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  IconButton,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridMoreVertIcon } from "@mui/x-data-grid-pro";

import { TextFieldLight } from "../../../../../common/components/styled/styled";
import { StyledTableCell } from "../../../../../common/components/functional/simpleTable/styled";
import { ActionMenu } from "./ActionMenu";

import { isFulfilled } from "@reduxjs/toolkit";
import { DatePicker } from "@mui/x-date-pickers";

import { format, isValid } from "date-fns";
import {
  DateRange,
  GarnishmentRestriction,
  TalentPaymentProhibition,
} from "../paymentProhibitionAPI";
import { useTranslation } from "react-i18next";
import { CustomerOrganizationsResult } from "../../../../customer/types/queries";
import { fetchEezyCompaniesAutocomplete } from "../../../../talents/talentsSlice";
import { useAppDispatch } from "../../../../../app/hooks";
import Exceptions from "./Exceptions/Exceptions";
import { AccessRightContext } from "../../../../user/AccessRightProvider";

interface Props {
  item: TalentPaymentProhibition;
  onDelete: (id: string) => void;
  paymentProhibitions: TalentPaymentProhibition[];
  setPaymentProhibitions: (
    paymentProhibition: TalentPaymentProhibition[],
  ) => void;
  editedItemId: string | undefined;
  onEditingRow: (id: string | null) => void;
  existingCompaniesWithOpenEndDates: string[];
}

export const PaymentProhibitionRowItem = ({
  item,
  onDelete,
  paymentProhibitions,
  setPaymentProhibitions,
  editedItemId,
  onEditingRow,
  existingCompaniesWithOpenEndDates,
}: Props) => {
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const dispatch = useAppDispatch();
  const { canEditUnionPayment } = useContext(AccessRightContext);

  const [companyInputValue, setCompanyInputValue] = useState<string>("");

  const { t } = useTranslation();

  const [companySearchHistory, setCompanySearchHistory] = useState<
    CustomerOrganizationsResult[]
  >([]);

  const deductionTypes = [
    { value: 1, label: t("talentProfile:normalDeduction") },
    { value: 2, label: t("talentProfile:deductionMin") },
    { value: 3, label: t("talentProfile:deductionMax") },
    { value: 4, label: t("talentProfile:oneTimeDeduction") },
    { value: 5, label: t("talentProfile:additionalWitholding") },
  ];

  useEffect(() => {
    const companyName = companySearchHistory?.find(
      (org: CustomerOrganizationsResult) => org.id === item.employer,
    )?.name;
    // Initialize input value with newItem.employerName if it exists
    setCompanyInputValue(item.employerName || companyName || "");
    // eslint-disable-next-line
  }, [item.employer, item.employerName, editedItemId]);

  const handleInputChange = (value: string | number, field: string) => {
    const updatedPaymentProhibitions = paymentProhibitions.map(
      (prohibition: TalentPaymentProhibition) => {
        if (prohibition.id === item.id) {
          // Check if we're updating the deductionType to 1, and if so, reset deductionTypeAmount
          if (field === "deductionType" && value === 1) {
            return {
              ...prohibition,
              [field]: value,
              deductionTypeAmount: undefined, // Reset to undefined if deductionType is 1
            };
          } else {
            // For all other updates, simply update the field without touching deductionTypeAmount
            return {
              ...prohibition,
              [field]: value,
            };
          }
        }
        return prohibition;
      },
    );
    setPaymentProhibitions([...updatedPaymentProhibitions]);
  };

  const handleDateChange = (date: any, field: "start" | "end") => {
    // If the date is valid, update the item in paymentProhibitions with the new date
    if (date === null || (date && isValid(date))) {
      const updatedPaymentProhibitions = paymentProhibitions.map(
        (prohibition) => {
          if (prohibition.id === item.id) {
            const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
            return { ...prohibition, [field]: formattedDate };
          }
          return prohibition;
        },
      );

      // Update the state with the modified array
      setPaymentProhibitions(updatedPaymentProhibitions);
    }
  };

  const deleteException = (exceptionId: string, type: string) => {
    const updatedRows = paymentProhibitions.map(
      (rowItem: TalentPaymentProhibition) => {
        if (rowItem.id === item.id) {
          // Delete matching exception from item that shares correct id and type.
          // Then update payment prohibitions state
          const updatedFreeMonths =
            type === "freeMonth"
              ? item.freeMonths?.filter(
                  (exception: DateRange) => exception.id !== exceptionId,
                ) ?? []
              : item.freeMonths;

          const updatedGarnishments =
            type === "garnishmentRestriction"
              ? item.garnishmentRestrictions?.filter(
                  (exception: GarnishmentRestriction) =>
                    exception.id !== exceptionId,
                ) ?? []
              : item.garnishmentRestrictions;

          return {
            ...rowItem,
            // Directly use updatedFreeMonths and updatedGarnishments since they are already conditionally defined
            freeMonths: updatedFreeMonths,
            garnishmentRestrictions: updatedGarnishments,
          };
        } else {
          return rowItem;
        }
      },
    );
    // Update state
    setPaymentProhibitions([...updatedRows]);
  };

  const handleCompanyChange = (value: CustomerOrganizationsResult) => {
    const updatedPaymentProhibitions = paymentProhibitions.map(
      (prohibition: TalentPaymentProhibition) => {
        if (prohibition.id === item.id) {
          return {
            ...prohibition,
            employer: value.id,
            employerName: value.name,
          };
        }
        return prohibition;
      },
    );

    setPaymentProhibitions([...updatedPaymentProhibitions]);
  };

  const handleCompanyValChange = (value: string) => {
    value &&
      dispatch(fetchEezyCompaniesAutocomplete({ name: value })).then(
        (action: any) => {
          if (isFulfilled(action)) {
            const payloadItems = action.payload;

            // Check if newItem's id is not empty and not already in the search history
            const newItemExists =
              item.employer &&
              !payloadItems.some(
                (org: CustomerOrganizationsResult) => org.id === item.employer,
              );

            // Only add newItem to the search history if it passes the checks
            const updatedItems = newItemExists
              ? [
                  ...payloadItems,
                  {
                    id: item.employer,
                    name: item.employerName,
                    businessId: "",
                    customerCompanyId: "",
                    customerCompanyBusinessId: "",
                    isRepresentative: false,
                    salesPermission: { verificationDate: "" },
                  },
                ]
              : payloadItems;

            setCompanySearchHistory(updatedItems);
          }
        },
      );
  };

  const handleExceptionChange = (
    value: string | Date | null,
    exceptionId: string,
    type: string,
    field: string,
  ) => {
    if (type === "freeMonth" && item?.freeMonths) {
      const newFreeMonths = item.freeMonths.map((fMonth) => {
        if (fMonth.id === exceptionId && value instanceof Date) {
          // Ensure value is a Date object; format both start and end dates
          const start = new Date(value.getFullYear(), value.getMonth(), 1);
          const formattedStart = format(start, "yyyy-MM-dd");
          const end = new Date(value.getFullYear(), value.getMonth() + 1, 0);
          const formattedEnd = format(end, "yyyy-MM-dd");
          return {
            ...fMonth,
            start: formattedStart,
            end: formattedEnd,
          };
        }
        return fMonth;
      });
      setPaymentProhibitions([
        ...paymentProhibitions.map((prohibition: TalentPaymentProhibition) => {
          if (prohibition.id === item.id) {
            return { ...prohibition, freeMonths: newFreeMonths };
          }
          return prohibition;
        }),
      ]);
    } else if (item?.garnishmentRestrictions) {
      const newGarnishmentRestrictions = item.garnishmentRestrictions.map(
        (gRest) => {
          if (gRest.id === exceptionId) {
            return {
              ...gRest,
              [field]: value,
            };
          }
          return gRest;
        },
      );

      setPaymentProhibitions([
        ...paymentProhibitions.map((prohibition: TalentPaymentProhibition) => {
          if (prohibition.id === item.id) {
            return {
              ...prohibition,
              garnishmentRestrictions: newGarnishmentRestrictions,
            };
          }
          return prohibition;
        }),
      ]);
    }
  };

  // Check if item comes from integration with limited edit options
  const isSystemItem = item.type === "SYSTEM";

  // Check if item is edited item
  const isEditedItem = editedItemId === item.id;

  const startDate = item?.start ? new Date(item.start) : null;
  const endDate = item?.end ? new Date(item.end) : null;

  return (
    <>
      <TableRow>
        <StyledTableCell>
          <DatePicker
            disabled={!isEditedItem || isSystemItem}
            format="dd.MM.yyyy"
            value={startDate}
            onChange={(date) => handleDateChange(date, "start")}
            slotProps={{
              textField: {
                size: "small",
                label: null,
                error:
                  (startDate && endDate && startDate > endDate) ||
                  (endDate && !startDate) ||
                  false,
                sx: {
                  width: "135px",
                  paddingTop: "0px",
                  border: "none",
                },
              },
            }}
          />
        </StyledTableCell>
        <StyledTableCell>
          <DatePicker
            disabled={!isEditedItem}
            format="dd.MM.yyyy"
            value={endDate}
            onChange={(date) => handleDateChange(date, "end")}
            slotProps={{
              textField: {
                size: "small",
                label: null,
                error:
                  (startDate && endDate && startDate > endDate) ||
                  (endDate && !startDate) ||
                  false,
                sx: {
                  width: "135px",
                  paddingTop: "0px",
                  border: "none",
                },
              },
            }}
          />
        </StyledTableCell>
        <StyledTableCell>
          <Tooltip title={item.enforcementAgency} placement="top">
            <TextFieldLight
              disabled={!isEditedItem || isSystemItem}
              size="small"
              value={item?.enforcementAgency || ""}
              onChange={(e) =>
                handleInputChange(e.target.value, "enforcementAgency")
              }
            />
          </Tooltip>
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            disabled={!isEditedItem || isSystemItem}
            size="small"
            value={item?.iban || ""}
            onChange={(e) => handleInputChange(e.target.value, "iban")}
          />
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            disabled={!isEditedItem || isSystemItem}
            size="small"
            value={item?.bic || ""}
            onChange={(e) => handleInputChange(e.target.value, "bic")}
          />
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            disabled={!isEditedItem || isSystemItem}
            size="small"
            value={item?.rfReference || ""}
            onChange={(e) => handleInputChange(e.target.value, "rfReference")}
          />
        </StyledTableCell>
        <StyledTableCell>
          <Tooltip title={item.employerName} placement="top">
            <Autocomplete
              disabled={!isEditedItem || isSystemItem}
              sx={{ width: "200px" }}
              size="small"
              fullWidth
              inputValue={companyInputValue}
              disablePortal
              ListboxProps={{
                style: { maxHeight: "300px", overflow: "visible" },
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t("talentProfile:noResults")}
              getOptionLabel={(option) => option.name}
              filterOptions={createFilterOptions({
                stringify: (option) => JSON.stringify(option),
              })}
              options={companySearchHistory}
              onChange={(_, newValue: CustomerOrganizationsResult | null) => {
                if (
                  newValue &&
                  !existingCompaniesWithOpenEndDates.includes(newValue.id)
                ) {
                  handleCompanyChange(newValue);
                } else {
                  handleCompanyChange({
                    id: "",
                    name: "",
                    businessId: "",
                    customerCompanyId: "",
                    customerCompanyBusinessId: "",
                    isRepresentative: false,
                    salesPermission: { verificationDate: "" },
                  });
                  setCompanyInputValue("");
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                // Update state based on user input, avoid action on selection to keep inputValue controlled
                if (reason === "input") {
                  setCompanyInputValue(newInputValue);
                  handleCompanyValChange(newInputValue);
                }
              }}
              renderOption={(props, option) => {
                // Check if the option is in the existingCompaniesWithOpenEndDates array
                const isDisabled = existingCompaniesWithOpenEndDates.includes(
                  option.id,
                );

                const typography = (
                  <Typography
                    sx={{
                      textTransform: !companySearchHistory.length
                        ? "uppercase"
                        : "unset",
                    }}
                    component="div"
                    variant="body2"
                    style={{
                      // Disabled style
                      color: isDisabled ? "grey" : "inherit",
                    }}
                  >
                    {option.name}
                  </Typography>
                );

                return (
                  <ListItem
                    {...props}
                    style={{ opacity: isDisabled ? 0.5 : 1 }}
                  >
                    <ListItemText
                      primary={
                        isDisabled ? (
                          <Tooltip
                            title={t(
                              "talentProfile:companyHasOpenPaymentProhibition",
                            )}
                            placement="left"
                          >
                            {typography}
                          </Tooltip>
                        ) : (
                          typography
                        )
                      }
                    />
                  </ListItem>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    t("talentProfile:findEezyCompany") || "Hae liitto"
                  }
                  name="eezyCompany"
                  required
                />
              )}
            />
          </Tooltip>
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            disabled={!isEditedItem || isSystemItem}
            size="small"
            value={item?.externalMonthlyNetIncome?.toString() || ""}
            onChange={(e) =>
              handleInputChange(e.target.value, "externalMonthlyNetIncome")
            }
          />
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            error={
              // If deductionType is 2 and dailyProtectedPortion is empty, 0, not a number, or contains invalid characters, show error
              item?.deductionType === 2 &&
              (!item.dailyProtectedPortion ||
                parseFloat(
                  item.dailyProtectedPortion.toString().replace(",", "."),
                ) <= 0 ||
                !/^\d*\.?\d+$/.test(
                  item.dailyProtectedPortion.toString().replace(",", "."),
                ))
            }
            disabled={!isEditedItem || isSystemItem}
            size="small"
            value={item?.dailyProtectedPortion?.toString() || ""}
            onChange={(e) =>
              handleInputChange(e.target.value, "dailyProtectedPortion")
            }
          />
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            disabled={!isEditedItem || isSystemItem}
            size="small"
            value={item?.additionalDeduction?.toString() || ""}
            onChange={(e) =>
              handleInputChange(e.target.value, "additionalDeduction")
            }
          />
        </StyledTableCell>
        <StyledTableCell>
          <Tooltip
            title={
              deductionTypes.find(
                (deduction) => deduction.value === item.deductionType,
              )?.label || ""
            }
            placement="top"
          >
            <Select
              disabled={!isEditedItem || isSystemItem}
              size="small"
              fullWidth
              sx={{ background: "#f9f9fa", maxWidth: "160px" }}
              value={item.deductionType || ""}
              onChange={(e) =>
                handleInputChange(e.target.value, "deductionType")
              }
              renderValue={(option) => (
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {deductionTypes.find(
                    (deduction) => deduction.value === item.deductionType,
                  )?.label || ""}
                </Box>
              )}
            >
              {deductionTypes.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ color: "black" }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </StyledTableCell>
        <StyledTableCell>
          <TextFieldLight
            error={
              // If deductionType is not 1 and deductionTypeAmount is empty, 0, not a number, or contains invalid characters, show error
              item?.deductionType !== 1 &&
              (!item.deductionTypeAmount ||
                parseFloat(
                  item.deductionTypeAmount.toString().replace(",", "."),
                ) <= 0 ||
                !/^\d*\.?\d+$/.test(
                  item.deductionTypeAmount.toString().replace(",", "."),
                ))
            }
            disabled={!isEditedItem || item.deductionType === 1 || isSystemItem}
            size="small"
            value={item?.deductionTypeAmount?.toString() || ""}
            onChange={(e) =>
              handleInputChange(e.target.value, "deductionTypeAmount")
            }
          />
        </StyledTableCell>
        {canEditUnionPayment && editedItemId === undefined && (
          <>
            <StyledTableCell>
              <IconButton onClick={(e) => setActionMenuAnchor(e.currentTarget)}>
                <GridMoreVertIcon />
              </IconButton>
            </StyledTableCell>
            <ActionMenu
              open={!!actionMenuAnchor}
              anchorEl={actionMenuAnchor}
              onClose={() => setActionMenuAnchor(null)}
              rowId={item.id}
              isRowEditable={true}
              onDeleteRow={onDelete}
              onEditingRow={onEditingRow}
              editingMode={isEditedItem}
              systemItem={isSystemItem}
            />
          </>
        )}
      </TableRow>

      {((item?.garnishmentRestrictions &&
        item?.garnishmentRestrictions?.length > 0) ||
        (item?.freeMonths && item?.freeMonths?.length > 0)) && (
        <TableRow>
          <TableCell
            colSpan={3}
            sx={{ padding: "0px 0px 10px 0px", border: "none" }}
          >
            <Exceptions
              deleteException={deleteException}
              handleExceptionChange={handleExceptionChange}
              editing={isEditedItem}
              rowItem={item}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
